/**
 * Native report group access — the per-user grant that decides which report
 * groups ('financial' | 'operations' | 'revenue') a user may see, and which
 * financial tooling rides along with them.
 *
 * Kept in its own module (no icon imports) so route guards, the sidebar and
 * tests can pull it without dragging the icon catalog along.
 */

/**
 * The active org's native report groups for this user, emulation-safe.
 *
 * initData's org payload (`currentOrganization.access`) reflects the EMULATED
 * user, while the raw `userSettings.orgAccess` doc does not — so the org
 * payload wins and the doc is the fallback (same precedence JournalExportPage
 * uses for journalPermission). Returns null = unrestricted.
 */
export function resolveNativeReportGroups(currentOrganization, userSettings) {
  const groups = currentOrganization?.access?.nativeReports
    ?? userSettings?.orgAccess?.[currentOrganization?.code]?.nativeReports;
  return Array.isArray(groups) ? groups.map((g) => String(g || '').trim().toLowerCase()) : null;
}

/**
 * Does this user hold a native report group? A non-array group list means the
 * grants were unresolvable (transition posture) = unrestricted = true.
 */
export function hasNativeReportGroup(nativeGroups, group) {
  if (!Array.isArray(nativeGroups)) return true;
  return nativeGroups.includes(String(group || '').trim().toLowerCase());
}

/** The admin-assignable "Budgeting & Forecasting" group (orgAccess.nativeReports). */
export const FORECASTING_GROUP = 'forecasting';

/**
 * Has an admin explicitly granted Budgeting & Forecasting to this user for the
 * active org? Unlike the report groups this NEVER fails open: the group is
 * only ever written by the admin form (no PBI-derived equivalent), so an
 * unresolvable list means "not granted", not "unrestricted".
 */
export function hasForecastingGrant(nativeGroups) {
  return Array.isArray(nativeGroups) && nativeGroups.includes(FORECASTING_GROUP);
}

/**
 * May this user open the Budgeting & Forecasting workspace (/forecast-v3)?
 *
 * Two independent doors, either one suffices:
 *  - the legacy org rollout — PostHog `forecasting` flag AND the Financial
 *    report group (what every current user came in through; kept as-is), or
 *  - the explicit per-user grant from User setup → Report Access.
 *
 * `forecastingFlag` is the raw useFeatureFlagEnabled value (undefined while
 * PostHog loads) — only a definite true opens the legacy door.
 */
export function canAccessForecasting(nativeGroups, forecastingFlag) {
  if (hasForecastingGrant(nativeGroups)) return true;
  return forecastingFlag === true && hasNativeReportGroup(nativeGroups, 'financial');
}

/**
 * May this user open the legacy Budgeting page (/budgeting)? PostHog
 * `budgeting` flag OR the same explicit grant — the grant covers both tools.
 */
export function canAccessBudgeting(nativeGroups, budgetingFlag) {
  return hasForecastingGrant(nativeGroups) || budgetingFlag === true;
}
